import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, X, Inbox } from 'lucide-react';
import * as tienda from '@/api/tienda.api';
import type { PropuestaDelAgente } from '@/api/tienda.api';
import { DataTable, type Columna } from '@/components/datos/DataTable';
import { ConDetalle } from '@/components/datos/DetailDrawer';
import { EntityLink } from '@/components/datos/EntityLink';
import { useAbrirDetalle, type Entidad } from '@/components/datos/contexto-detalle';
import { ModuleHeader, Kpi } from '@/components/datos/ModuleHeader';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/hooks/useToast';
import { numero, fecha, haceCuanto } from '@/lib/formato-tienda';
import { DetalleDeVenta, DetalleDeProducto, DetalleDeCliente } from '@/components/datos/detalles';

/**
 * LO QUE PIDE EL AGENTE — la bandeja donde el bot pide permiso.
 *
 * ═══ POR QUÉ ESTA PANTALLA EXISTE ═══
 *
 * El agente puede guardar una clienta, mirar su historial y armar un borrador.
 * Las cuatro cosas que mueven inventario o plata —anular, devolver, ajustar
 * existencia y marcar cobrada— NO tienen herramienta: el modelo no puede
 * hacerlas aunque se lo pidan, porque no hay función que las haga. Lo único
 * que puede es dejarlas escritas aquí.
 *
 * ═══ Y POR QUÉ «APROBAR» NO EJECUTA NADA ═══
 *
 * Porque sería devolverle por la puerta de atrás la herramienta que decidimos
 * no darle. Aprobar cierra la propuesta y te lleva a la venta; la acción la
 * haces tú con el mismo botón de siempre —el que avisa cuántas piezas vuelven
 * al inventario antes de que la pulses.
 *
 * Suena a paso de más. Es el paso que hace que un error del modelo sea un
 * mensaje que ignoras, y no una venta anulada que hay que reconstruir.
 */

const QUE: Record<PropuestaDelAgente['que'], { etiqueta: string; donde: string }> = {
  anular_venta: { etiqueta: 'Anular una venta', donde: 'Ventas → Anular todo' },
  devolver: { etiqueta: 'Devolver piezas', donde: 'Ventas → Devolver' },
  ajustar_existencia: { etiqueta: 'Ajustar existencia', donde: 'Inventario → Mover existencia' },
  marcar_cobrada: { etiqueta: 'Marcar una venta cobrada', donde: 'Ventas → Marcar cobrada' },
};

export default function Propuestas() {
  return (
    <ConDetalle render={(e) => <Detalle entidad={e} />}>
      <Contenido />
    </ConDetalle>
  );
}

function Detalle({ entidad }: { entidad: Entidad }) {
  if (entidad.tipo === 'venta') return <DetalleDeVenta id={entidad.id} />;
  if (entidad.tipo === 'producto') return <DetalleDeProducto id={entidad.id} />;
  if (entidad.tipo === 'cliente') return <DetalleDeCliente id={entidad.id} />;
  return <p className="text-sm text-slate-500">Nada que mostrar.</p>;
}

function Contenido() {
  const abrir = useAbrirDetalle();
  const toast = useToast();
  const qc = useQueryClient();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['tienda', 'propuestas'],
    queryFn: () => tienda.propuestas(),
  });
  const propuestas = data ?? [];

  const responder = useMutation({
    mutationFn: ({ id, estado }: { id: string; estado: 'aprobada' | 'descartada' }) =>
      tienda.responderPropuesta(id, estado),
    onSuccess: (_r, v) => {
      toast.success(
        v.estado === 'aprobada'
          ? 'Cerrada. Ahora haz la acción en su pantalla.'
          : 'Descartada.',
      );
      void qc.invalidateQueries({ queryKey: ['tienda', 'propuestas'] });
      void qc.invalidateQueries({ queryKey: ['tienda', 'bitacora'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo'),
  });

  const columnas: Columna<PropuestaDelAgente>[] = [
    {
      id: 'cuando',
      titulo: 'Cuándo',
      fija: true,
      ancho: 'w-32',
      valor: (p) => p.creada,
      celda: (p) => (
        <span title={fecha(p.creada, true)} className="text-slate-600 dark:text-slate-300">
          {haceCuanto(p.creada)}
        </span>
      ),
    },
    {
      id: 'que',
      titulo: 'Qué pide',
      valor: (p) => QUE[p.que]?.etiqueta ?? p.que,
      celda: (p) => (
        <div className="min-w-0">
          <p className="font-medium text-slate-900 dark:text-slate-100">
            {QUE[p.que]?.etiqueta ?? p.que}
          </p>
          <p className="text-xs text-slate-400">lo haces en {QUE[p.que]?.donde ?? 'su pantalla'}</p>
        </div>
      ),
    },
    {
      id: 'referencia',
      titulo: 'Sobre qué',
      valor: (p) => p.referencia ?? '',
      celda: (p) =>
        p.referencia ? (
          <EntityLink tipo="venta" id={p.referencia} etiqueta="la venta" />
        ) : (
          <span className="text-slate-400">—</span>
        ),
    },
    {
      id: 'porque',
      titulo: 'Por qué lo pide',
      valor: (p) => p.porque,
      celda: (p) => (
        <span className="text-slate-700 dark:text-slate-200">{p.porque}</span>
      ),
    },
    {
      id: 'detalle',
      titulo: 'Detalle',
      ocultaPorDefecto: true,
      valor: (p) => p.detalle ?? '',
      celda: (p) => (
        <span className="text-slate-600 dark:text-slate-300">{p.detalle ?? '—'}</span>
      ),
    },
    {
      id: 'estado',
      titulo: 'Estado',
      ancho: 'w-28',
      valor: (p) => p.estado,
      celda: () => <Badge variant="warning">Pendiente</Badge>,
    },
  ];

  return (
    <div className="space-y-5">
      <ModuleHeader
        titulo="Lo que pide el agente"
        subtitulo="Las acciones que mueven inventario o plata no las hace el bot: las pide aquí y las haces tú."
        kpis={
          <>
            <Kpi
              titulo="Pendientes"
              valor={numero(propuestas.length)}
              pie="esperan que decidas"
              tono={propuestas.length > 0 ? 'malo' : 'bueno'}
              bajarEsBueno
            />
            <Kpi
              titulo="Más vieja"
              valor={
                propuestas.length
                  ? haceCuanto(propuestas[propuestas.length - 1]!.creada)
                  : '—'
              }
            />
            <Kpi
              titulo="Sobre ventas"
              valor={numero(
                propuestas.filter(
                  (p) => p.que === 'anular_venta' || p.que === 'devolver' || p.que === 'marcar_cobrada',
                ).length,
              )}
            />
            <Kpi
              titulo="Sobre inventario"
              valor={numero(propuestas.filter((p) => p.que === 'ajustar_existencia').length)}
            />
          </>
        }
      />

      <DataTable
        filas={propuestas}
        columnas={columnas}
        idDe={(p) => p.id}
        cargando={isLoading}
        error={isError ? (error instanceof Error ? error.message : 'Error') : null}
        onReintentar={() => void refetch()}
        memoria="propuestas"
        onAbrirFila={(p) =>
          p.referencia ? abrir?.({ tipo: 'venta', id: p.referencia, etiqueta: 'la venta' }) : undefined
        }
        accionesDeFila={[
          {
            etiqueta: 'Ya lo resolví',
            icono: <Check className="h-3.5 w-3.5" />,
            onClick: (p) => responder.mutate({ id: p.id, estado: 'aprobada' }),
          },
          {
            etiqueta: 'Descartar',
            icono: <X className="h-3.5 w-3.5" />,
            peligrosa: true,
            onClick: (p) => responder.mutate({ id: p.id, estado: 'descartada' }),
          },
        ]}
        vacio={{
          titulo: 'El agente no ha pedido nada',
          descripcion:
            'Cuando una clienta le pida anular, devolver o ajustar algo, el pedido va a aparecer aquí con su motivo.',
        }}
      />

      <p className="flex items-start gap-2 text-xs text-slate-500">
        <Inbox className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          «Ya lo resolví» cierra el pedido y lo firma en la bitácora, pero no ejecuta nada. Es a
          propósito: el bot no tiene herramienta para anular ni ajustar, y darle una por aquí sería
          quitarle el candado por la puerta de atrás.
        </span>
      </p>
    </div>
  );
}
